/**
 * Pull-to-refresh для скролл-контейнера.
 *
 * Хук вешает touch-listeners на переданный ref-элемент (scroll-container).
 * Когда пользователь начинает свайп от верха (scrollTop === 0) и тянет вниз
 * больше TRIGGER_PX, после отпускания вызывается onRefresh().
 *
 * Возвращает state для рендера индикатора (translateY/прогресс).
 *
 * Принципиально не двигаем сам контейнер — отдельный indicator-блок над ним
 * рисует Shell. Это надёжнее: не ломает scroll-momentum, не конфликтует с iOS-bounce.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

const TRIGGER_PX = 80;
const MAX_PULL = 140;

export interface PullState {
  /** Сколько пикселей оттянуто (0..MAX_PULL). */
  pull: number;
  /** true — палец отпущен, идёт refetch. */
  refreshing: boolean;
  /** true — оттянуто достаточно, чтобы при отпускании сработал refresh. */
  ready: boolean;
}

export function usePullToRefresh(
  scrollEl: HTMLElement | null,
  onRefresh: (() => unknown | Promise<unknown>) | null,
): PullState {
  const [state, setState] = useState<PullState>({ pull: 0, refreshing: false, ready: false });
  const startY = useRef<number | null>(null);
  const activeRef = useRef(false);

  const reset = useCallback(() => {
    startY.current = null;
    activeRef.current = false;
    setState((s) => ({ ...s, pull: 0, ready: false }));
  }, []);

  useEffect(() => {
    if (!scrollEl || !onRefresh) return;

    const onTouchStart = (e: TouchEvent) => {
      if (state.refreshing) return;
      if (scrollEl.scrollTop > 0) return;
      const t = e.touches[0];
      if (!t) return;
      startY.current = t.clientY;
      activeRef.current = false;
    };

    const onTouchMove = (e: TouchEvent) => {
      if (state.refreshing) return;
      if (startY.current === null) return;
      const t = e.touches[0];
      if (!t) return;
      const dy = t.clientY - startY.current;

      // Если контейнер успел скрольнуться вниз — отменяем
      if (scrollEl.scrollTop > 0) {
        reset();
        return;
      }

      if (dy <= 0) return;

      // Резинка: чем дальше — тем медленнее.
      const pull = Math.min(MAX_PULL, dy * 0.55);
      activeRef.current = true;
      setState({
        pull,
        refreshing: false,
        ready: pull >= TRIGGER_PX,
      });

      // Блокируем нативное поведение overscroll, если действительно тянем.
      if (e.cancelable) e.preventDefault();
    };

    const onTouchEnd = async () => {
      if (state.refreshing) return;
      const wasActive = activeRef.current;
      const ready = state.ready;
      startY.current = null;
      activeRef.current = false;

      if (!wasActive || !ready) {
        setState({ pull: 0, refreshing: false, ready: false });
        return;
      }

      // Запускаем refetch
      setState({ pull: TRIGGER_PX * 0.7, refreshing: true, ready: false });
      try {
        await onRefresh();
      } catch {
        // ignore — toast покажется через query error
      } finally {
        setState({ pull: 0, refreshing: false, ready: false });
      }
    };

    scrollEl.addEventListener('touchstart', onTouchStart, { passive: true });
    // touchmove с passive: false, чтобы preventDefault работал
    scrollEl.addEventListener('touchmove', onTouchMove, { passive: false });
    scrollEl.addEventListener('touchend', onTouchEnd);
    scrollEl.addEventListener('touchcancel', onTouchEnd);

    return () => {
      scrollEl.removeEventListener('touchstart', onTouchStart);
      scrollEl.removeEventListener('touchmove', onTouchMove);
      scrollEl.removeEventListener('touchend', onTouchEnd);
      scrollEl.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [scrollEl, onRefresh, state.refreshing, state.ready, reset]);

  return state;
}

export const PTR_TRIGGER_PX = TRIGGER_PX;
